// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * bot.ts — placement policies for the local race opponents AND the balance sim.
 *
 * A policy chooses, for the current falling brick, a rotation and a target x,
 * steers the brick there and lets it settle. The sim runs the same policies
 * AI-vs-AI to referee the game (principle #18): a BETTER policy must beat a
 * WORSE one well above chance, or the physics is a slot machine wearing a tower.
 * In the descent model the skill axis is PRECISION — a sloppy or reckless hand
 * places bricks that miss the support and fall, so it cannot compete.
 *
 * Policies read only the public surface of `Game`, and take an `rng` so per-seat
 * jitter can differ without touching the shared seed.
 */

import type { Game } from './game';
import { shapeExtent } from './pieces';
import type { Rng } from '@ben-gy/game-engine/rng';

export type PolicyId = 'steady' | 'stacker' | 'wide' | 'random';

export interface BotPreset {
  id: PolicyId;
  name: string;
  jitter: number; // world-unit spread on the chosen x
}

export const BOT_PRESETS: Record<PolicyId, BotPreset> = {
  stacker: { id: 'stacker', name: 'Stacker', jitter: 3 },
  steady: { id: 'steady', name: 'Steady', jitter: 7 },
  wide: { id: 'wide', name: 'Spread', jitter: 12 },
  random: { id: 'random', name: 'Reckless', jitter: 0 },
};

/** Rotate the preview to a target rotation using the smallest number of turns. */
function rotateTo(game: Game, target: number): void {
  let guard = 0;
  while (game.curRot() !== ((target % 4) + 4) % 4 && guard < 4) {
    game.rotate(1);
    guard += 1;
  }
}

/** The rotation (0..3) with the widest, flattest footprint — most stable base. */
function flatRot(game: Game): number {
  const shape = game.curShape();
  let best = 0;
  let bestW = -1;
  for (let r = 0; r < 4; r++) {
    const { w, h } = shapeExtent(shape, r);
    const score = w - h * 0.5; // prefer wide and short
    if (score > bestW) { bestW = score; best = r; }
  }
  return best;
}

/** The rotation with the narrowest footprint — build straight up. */
function tallRot(game: Game): number {
  const shape = game.curShape();
  let best = 0;
  let bestW = Infinity;
  for (let r = 0; r < 4; r++) {
    const { w } = shapeExtent(shape, r);
    if (w < bestW) { bestW = w; best = r; }
  }
  return best;
}

/**
 * Play ONE brick with a policy: pick a rotation and a target x, steer the
 * falling brick there and let it settle. Returns once the brick resolves and the
 * pile reacts. `_tick` is kept for call-site compatibility (per-seat scheduling).
 */
export function botPlay(game: Game, preset: BotPreset, rng: Rng, _tick = 0): void {
  if (game.over) return;
  const sup = game.support();
  const bounds = game.aimBounds();
  const clamp = (x: number) => Math.max(bounds.min, Math.min(bounds.max, x));
  const jit = (preset.jitter ? (rng() * 2 - 1) * preset.jitter : 0);

  // In the descent model there is no wide-base strategy — a brick placed out to
  // the side lands on air and falls. The skill axis is PRECISION: every policy
  // stacks over the tower's support, and a better one simply misses by less. The
  // "pull toward the plinth centre" keeps a bot from drifting a tall tower off a
  // narrow plinth (it matters most in Spire).
  const PLAY_W = game.worldConst().PLAY_W;
  const aimCentre = sup.centroidX * 0.85 + (PLAY_W / 2) * 0.15;
  let targetX: number;
  switch (preset.id) {
    case 'random':
      rotateTo(game, Math.floor(rng() * 4));
      targetX = bounds.min + rng() * (bounds.max - bounds.min);
      break;
    case 'stacker':
      rotateTo(game, tallRot(game));   // narrow footprint, straight up
      targetX = clamp(aimCentre + jit);
      break;
    case 'wide':                        // flat pieces, but a sloppy hand
      rotateTo(game, flatRot(game));
      targetX = clamp(aimCentre + jit);
      break;
    case 'steady':
    default:
      rotateTo(game, flatRot(game));
      targetX = clamp(aimCentre + jit);
      break;
  }

  game.aimTo(targetX);
  game.dropAndSettle();
}